import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/Layout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { sideItemsApi } from "../../api/sideItems";
import Swal from "sweetalert2";

interface SideItem {
  _id: string;
  name: string;
  price: number;
  category: "drink" | "dip" | "dressing" | "other";
  visibleInMenu: boolean;
  available: boolean;
}

const SideItems = () => {
  const [sides, setSides] = useState<SideItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingSide, setEditingSide] = useState<SideItem | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    price: 0,
    category: "dressing" as "drink" | "dip" | "dressing" | "other",
    visibleInMenu: true,
    available: true,
  });

  const fetchSides = async () => {
    try {
      setLoading(true);
      const data = await sideItemsApi.getSideItems();
      setSides(data.sides || []);
    } catch (error) {
      console.error("Failed to fetch side items", error);
      Swal.fire("Error", "Could not fetch side items", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSides();
  }, []);

  const handleOpenModal = (side?: SideItem) => {
    if (side) {
      setEditingSide(side);
      setFormData({
        name: side.name,
        price: side.price,
        category: side.category,
        visibleInMenu: side.visibleInMenu,
        available: side.available,
      });
    } else {
      setEditingSide(null);
      setFormData({
        name: "",
        price: 0,
        category: "dressing",
        visibleInMenu: true,
        available: true,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSide) {
        await sideItemsApi.updateSideItem(editingSide._id, formData);
        Swal.fire("Success", "Side item updated successfully", "success");
      } else {
        await sideItemsApi.createSideItem(formData);
        Swal.fire("Success", "Side item created successfully", "success");
      }
      handleCloseModal();
      void fetchSides();
    } catch (error) {
      Swal.fire("Error", "Failed to save side item", "error");
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This side item will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#334155",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        await sideItemsApi.deleteSideItem(id);
        Swal.fire("Deleted!", "Side item has been deleted.", "success");
        void fetchSides();
      } catch (error) {
        Swal.fire("Error", "Failed to delete side item", "error");
      }
    }
  };

  const handleToggleAvailable = async (row: SideItem) => {
    try {
      await sideItemsApi.updateSideItem(row._id, {
        available: !row.available,
      });
      void fetchSides();
    } catch (err) {
      Swal.fire("Error", "Could not toggle availability", "error");
    }
  };

  const handleToggleVisible = async (row: SideItem) => {
    try {
      await sideItemsApi.updateSideItem(row._id, {
        visibleInMenu: !row.visibleInMenu,
      });
      void fetchSides();
    } catch (err) {
      Swal.fire("Error", "Could not toggle visibility", "error");
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case "drink": return "primary";
      case "dressing": return "success";
      case "dip": return "warning";
      default: return "default";
    }
  };

  return (
    <Layout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Side Items & Add-ons
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{ borderRadius: 2 }}
          >
            Add Side Item
          </Button>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Visible in Menu</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Available (Kitchen)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sides.map((row) => (
                      <TableRow key={row._id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.category.toUpperCase()}
                            size="small"
                            color={getCategoryColor(row.category) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>₹{row.price}</TableCell>
                        <TableCell>
                          <Switch
                            checked={row.visibleInMenu}
                            color="primary"
                            onChange={() => handleToggleVisible(row)}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={row.available}
                            color="success"
                            onChange={() => handleToggleAvailable(row)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenModal(row)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(row._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sides.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                          No side items found. Click "Add Side Item" to create one!
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle sx={{ fontWeight: "bold" }}>
              {editingSide ? "Edit Side Item" : "Add New Side Item"}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={3} mt={1}>
                <TextField
                  label="Side Item Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                
                <TextField
                  label="Price (₹)"
                  type="number"
                  fullWidth
                  required
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: Number(e.target.value) || 0 })
                  }
                  helperText="Set to 0 if the item is free (e.g. required dressings)"
                />

                <FormControl fullWidth required>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    label="Category"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category: e.target.value as "drink" | "dip" | "dressing" | "other",
                      })
                    }
                  >
                    <MenuItem value="dressing">Dressing</MenuItem>
                    <MenuItem value="dip">Dip</MenuItem>
                    <MenuItem value="drink">Drink</MenuItem>
                    <MenuItem value="other">Other Side</MenuItem>
                  </Select>
                </FormControl>

                <Box display="flex" justifyContent="space-between" alignItems="center" p={1} bgcolor="#f8fafc" borderRadius={2}>
                  <Box>
                    <Typography fontWeight={500}>Visible in Menu</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Shows up in the global Sides section at the bottom of the user menu
                    </Typography>
                  </Box>
                  <Switch
                    checked={formData.visibleInMenu}
                    onChange={(e) =>
                      setFormData({ ...formData, visibleInMenu: e.target.checked })
                    }
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" p={1} bgcolor="#f8fafc" borderRadius={2}>
                  <Box>
                    <Typography fontWeight={500}>Available (Kitchen Toggle)</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Mark temporarily out of stock in the kitchen without deleting
                    </Typography>
                  </Box>
                  <Switch
                    checked={formData.available}
                    color="success"
                    onChange={(e) =>
                      setFormData({ ...formData, available: e.target.checked })
                    }
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleCloseModal} variant="outlined" color="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Save
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SideItems;
